import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { spacing } from 'ui/theme';
import { ErrorPage } from 'core/errorPage';
import { useAngularContext } from 'utils/contexts/angular';

const AngularContainer = styled.div`
  margin-top: -${spacing.doubleHalf};
  margin-left: -${spacing.double};
  margin-right: -${spacing.double};
`;

export type AngularTemplateProps = {
  /** The value of the `ng-template` property on the <template> element containing the Angular template */
  angularTemplateName?: string;

  /** The module name of the Angular component/app being mounted */
  angularModuleName?: string;
};

/**
 * Component for mounting an Angular component within a React component tree. Cleans up mounted
 * Angular module's async subscriptions/event listeners on unmount.
 */
export const AngularTemplate = ({
  angularTemplateName = 'main',
  angularModuleName = 'iterableApp',
  ...props
}: AngularTemplateProps) => {
  const { setScope } = useAngularContext();

  /**
   * On mount, look to see if the backend included a template in the page HTML with
   * the name specified by the prop `angularTemplateName`.
   */
  const angularTemplate = useMemo(
    () => document.querySelector(`[ng-template="${angularTemplateName}"]`),
    [],
  );

  /**
   * Create a ref through which the Angular template HTML will be injected into the
   * React DOM.
   */
  const containerRef = useRef<HTMLDivElement | null>(null);

  /**
   * Effect that only runs on mount/unmount. On mount, it injects the Angular template into the
   * React DOM and bootstraps the Angular app. On unmount, it destroys the Angular app's root
   * scope (to stop all Angular event listeners) and cleans up initialization data on the element object.
   */
  useEffect(() => {
    // Return no-op if Angular template is not found (causing <AngularContainer> not to be rendered)
    if (!containerRef.current) {
      return;
    }

    // Return no-op if Angular app has been initalized already
    const angularContainerElement = window.angular.element(containerRef.current);
    const initialized = !!angularContainerElement.injector();
    if (initialized) {
      return;
    }

    // Inject Angular template, bootstrap the Angular app
    let didCleanUpEffect = false;
    let didBootstrapAngularApp = false;
    angularContainerElement.html(angularTemplate?.innerHTML || '');

    // Bootstrap the Angular app if the container element becomes ready before this effect gets cleaned up
    angularContainerElement.ready(() => {
      if (!didCleanUpEffect) {
        didBootstrapAngularApp = true;
        window.angular.bootstrap(angularContainerElement, [angularModuleName]);
        setScope(angularContainerElement.scope());
      }
    });

    // Return cleanup code. Destroy Angular event listeners/async subscriptions and clean up initialization data
    return () => {
      didCleanUpEffect = true;
      if (didBootstrapAngularApp) {
        angularContainerElement.scope().$root.$destroy();
        angularContainerElement.removeData();
        setScope(null);
      }
    };
  }, []);

  // Render error if, on mount, the Angular template was not found in the DOM
  if (!angularTemplate) {
    return <ErrorPage header="Page Not Found" />;
  }

  return <AngularContainer ref={containerRef} data-test="angular-template" {...props} />;
};

export default AngularTemplate;
